import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ChevronRightIcon, PresentationChartLineIcon } from '@heroicons/react/24/outline';

import { selectMonthlyStatsData } from '../slices/reportsSlice';
import Metric from './ui/Metric';
import { leagueTier } from '../utils/statusTokens';

// Phone-only standing strip: latest daily rank, monthly standing and league,
// derived from the monthly stats payload the dashboard cards already fetch
// (no fetch of its own), plus the entry point into the cross-market pane.
// The pane row always renders for a forecaster so the phone never loses its
// only entry into that pane; the metric cells only show once stats for this
// user are in the store.
export default function MobileRankStrip({ userId, onOpenPane, className = '' }) {
  const monthlyStatsData = useSelector(selectMonthlyStatsData);

  const stats = useMemo(() => {
    if (!userId || !monthlyStatsData || monthlyStatsData.user_id !== userId) return null;

    const days = Array.isArray(monthlyStatsData.daily_ranks_json)
      ? monthlyStatsData.daily_ranks_json
          .filter((d) => d && d.rank != null && d.target_day)
          .sort((a, b) => String(a.target_day).localeCompare(String(b.target_day)))
      : [];
    const latest = days[days.length - 1] || null;
    const previous = days[days.length - 2] || null;
    // Positive delta = moved up the board (rank number went down).
    const delta = latest && previous ? previous.rank - latest.rank : null;

    const scores = Array.isArray(monthlyStatsData.month_scores_json)
      ? monthlyStatsData.month_scores_json
          .filter((row) => row && row.score != null)
          .sort((a, b) => Number(a.score) - Number(b.score))
      : [];
    // Unqualified forecasters keep their place but take no rank, the same
    // numbering as MonthlyLeaderboard below this strip.
    let qualifiedRank = 0;
    const selfRow = scores
      .map((row) => ({ ...row, rank: row.is_unqualified === true ? null : ++qualifiedRank }))
      .find((row) => row.user_id === userId);
    const monthlyRank = selfRow?.rank ?? null;

    if (!latest && monthlyRank == null) return null;
    return {
      latest,
      delta,
      monthlyRank,
      totalForecasters: scores.length,
      leagueId: monthlyStatsData.league_id,
      missed: monthlyStatsData.n_days_w_penalties ?? 0,
      maxMissed: monthlyStatsData.max_missed_days ?? 5,
    };
  }, [monthlyStatsData, userId]);

  const tier = stats ? leagueTier(stats.leagueId) : null;
  const deltaText =
    stats && stats.delta != null && stats.delta !== 0
      ? stats.delta > 0
        ? `▲ ${stats.delta} vs previous day`
        : `▼ ${Math.abs(stats.delta)} vs previous day`
      : null;

  return (
    <section
      aria-label={stats ? 'Your standing' : undefined}
      className={`lg:hidden rounded-xl border border-gray-200/70 bg-surface shadow-sm dark:border-gray-600/70 dark:bg-gray-870 ${className}`}
    >
      {stats && (
        <div className="grid grid-cols-[1fr_1fr_1.25fr] divide-x divide-gray-200/70 dark:divide-gray-600/70">
          <div className="min-w-0 px-3.5 py-3">
            <Metric
              label="Latest day"
              value={stats.latest ? `#${stats.latest.rank}` : '–'}
              sub={deltaText}
              subClassName={stats.delta > 0 ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}
            />
          </div>
          <div className="min-w-0 px-3.5 py-3">
            <Metric
              label="Month"
              value={stats.monthlyRank != null ? `#${stats.monthlyRank}` : '–'}
              sub={stats.monthlyRank != null ? `of ${stats.totalForecasters}` : null}
            />
          </div>
          <div className="min-w-0 px-3.5 py-3">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">League</div>
            <div className={`mt-1 min-w-0 text-base font-light leading-6 ${tier.text}`}>{tier.label}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{`${stats.missed} / ${stats.maxMissed} missed`}</div>
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={onOpenPane}
        className={`flex h-11 w-full items-center justify-between px-3.5 text-sm font-medium text-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 dark:text-brand-400 ${
          stats ? 'border-t border-gray-200/70 dark:border-gray-600/70' : ''
        }`}
      >
        <span className="flex items-center gap-2">
          <PresentationChartLineIcon className="h-[18px] w-[18px]" aria-hidden="true" />
          Cross-market performance
        </span>
        <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </section>
  );
}
